import type {UseQueryResult} from 'react-query';
import {useQuery} from 'react-query';
import {z} from 'zod';
import {apiUrl} from '@/utils/api';
import {useUserContext} from '@/components/Providers/UserContext';
import {type CourseApproval} from '@/types/CourseApproval';

type UseFetchCourseApprovalsResponse = {
    data : {
        approvals : CourseApproval[],
        hasApprovalAccess : boolean,
    },
    errorCode : number,
    message : string,
};

const materialRequirementSchema = z.object({
    description : z.string(),
    manufacturer : z.string(),
    subType : z.string(),
    type : z.string(),
});

const courseApprovalSchema = z.object({
    compoundKey: z.string(),
    compoundKeyType: z.string(),
    courseYearApprovalId: z.string(),
    sectionId: z.string(),
    approved: z.boolean(),
    courseNumber: z.string(),
    department: z.string(),
    materialRequirements: z.array(materialRequirementSchema),
    request: z.string(),
    schoolShortName: z.string(),
    sectionNumber: z.string(),
    year: z.string(),
});

const useFetchCourseApprovalsResponseSchema = z.object({
    data : z.object({
        approvals: z.array(courseApprovalSchema),
        hasApprovalAccess: z.boolean(),
    }),
    errorCode : z.number(),
    message : z.string(),
});

const useFetchCourseApprovals = () : UseQueryResult<UseFetchCourseApprovalsResponse> => {
    const {user, setUser} = useUserContext();

    return useQuery(
        ['course-approvals'],
        async ({signal}) => {
            const response = await fetch(
                apiUrl('/v1/course-approval').toString(),
                {
                    signal: (signal ?? null),
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user?.jwt}`,
                    },
                }
            );

            if (response.status === 401) {
                setUser(null);
            }

            if (!response.ok) {
                throw new Error('Unable to fetch course-approval.');
            }

            const json = await response.json() as UseFetchCourseApprovalsResponse;

            try {
                useFetchCourseApprovalsResponseSchema.parse(json);
            } catch (error: unknown) {
                console.log('error', error);
                throw new Error('Unable to parse course-approval response.');
            }

            return json;
        },
        {
            enabled: user !== undefined
        }
    );
};

export default useFetchCourseApprovals;
